import { SummaryData } from '../../../interfaces/summary-data';
import './MetricsDevices.scss';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { getLanguage } from '../../../util/languages';

import { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import { ArrowDown, ArrowUp, Clock, RotateCcw, Sigma, Wifi } from 'lucide-react';

import { chartTokens } from '../../../design-system/chartTokens';
import { fonts } from '../../../design-system/tokens';
import type { BDTheme } from '../../../design-system/types';

type Props = {
  summaryData: SummaryData;
};

interface DataPoint {
  x: Date;
  y: number;
}

// Info blue / violet are not in chartTokens; concrete per-theme values for apexcharts.
const INFO_BLUE: Record<BDTheme, string> = { dark: '#4ea4f4', light: '#1d6fd4' };

// 1536 -> "1.5 KB"
const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, i);
  return `${value >= 100 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
};

// Quick time windows for the traffic charts. `zoom` (days) is fed to the existing
// determineZoomLevel effects so each window gets a sensible groupBy:
// 1h -> minute, 6h -> 5min, 24h -> hour (the default 0.8), 7d -> day.
const RANGES: { label: string; hours: number; zoom: number }[] = [
  { label: '1h', hours: 1, zoom: 0.03 },
  { label: '6h', hours: 6, zoom: 0.1 },
  { label: '24h', hours: 24, zoom: 0.8 },
  { label: '7d', hours: 168, zoom: 4 },
];

// Signal gauge scale — matches getSignalStrength buckets
const SIGNAL_SCALE = ['Weak', 'Fair', 'Good', 'Strong'];

const readDdTheme = (): BDTheme =>
  document.querySelector('.dd-page')?.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';

// Follows the Device Details page's data-theme attribute (apexcharts needs concrete colours).
const useDdTheme = (): BDTheme => {
  const [theme, setTheme] = useState<BDTheme>(readDdTheme);
  useEffect(() => {
    setTheme(readDdTheme());
    const el = document.querySelector('.dd-page');
    if (!el) return;
    const observer = new MutationObserver(() => setTheme(readDdTheme()));
    observer.observe(el, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
  return theme;
};

export const MetricsDevices = (props: Props) => {
  const { deviceId } = useParams();
  const theme = useDdTheme();
  // `t` is taken by chartTokens below, so the translator is aliased
  const { t: translate, i18n } = useTranslation();
  const [graphLoading, setGraphLoading] = useState(true);
  const [pieLoading, setPieLoading] = useState(true);
  const [timeCaptured, setTimeCaptured] = useState('')
  const [signalCaptured, setSignalCaptured] = useState('')
  const [txCaptured, setTxCaptured] = useState('')
  const [rxCaptured, setRxCaptured] = useState('')

  const [txZoomLevel, setTxZoomLevel] = useState<string>('hour');
  const [txSelectedRange, setTxSelectedRange] = useState<number>(0.8);
  const [txZoomRange, setTxZoomRange] = useState<number>(txSelectedRange);

  const [rxZoomLevel, setRxZoomLevel] = useState<string>('hour');
  const [rxSelectedRange, setRxSelectedRange] = useState<number>(0.8);
  const [maxTxBitrate, setMaxTxBitrate] = useState<number>(0);
  const [maxRxBitrate, setMaxRxBitrate] = useState<number>(0);
  const [rxZoomRange, setRxZoomRange] = useState<number>(rxSelectedRange);

  const [activeRange, setActiveRange] = useState<number | null>(24);
  const [minX, setMinX] = useState<any>(new Date(new Date().getTime() - 24 * 60 * 60 * 1000).toISOString());
  const [maxX, setMaxX] = useState<any>(new Date().toISOString());

  useEffect(() => {
    const fetchGraphData = async () => {
      setGraphLoading(true);
      try {
        // console.log("date", minX, maxX)
        // console.log("groupBy", txZoomLevel)
        const txLineResponse = await axios.post(`/v2/op/device/${deviceId}/device-metrics/txBytes?startDate=${minX}&endDate=${maxX}`, {
          groupBy: txZoomLevel,
        });

        let txFormattedLineData: DataPoint[] = []

        for (let i = 0; i < txLineResponse.data.length; i++) {
          let date = new Date(txLineResponse.data[i].time_group)
          date.setHours(date.getHours() + 1)
          txFormattedLineData.push({ x: date, y: txLineResponse.data[i].txBytes })
        }
        // console.log(txLineResponse)
        // console.log(txFormattedLineData)
        let txLastdate = txFormattedLineData[txFormattedLineData.length - 1]
        if (txFormattedLineData.length === 1) {
          setTxChartData({
            ...txChartData,
            series: [{ name: 'txBytes', data: txFormattedLineData }],
            options: {
              ...txChartData.options,
              xaxis: {
                ...txChartData.options.xaxis,
                categories: txFormattedLineData,
                max: new Date(maxX).getTime(),
              },
            },
          });
        } else {
          setTxChartData({
            ...txChartData,
            series: [{ name: 'txBytes', data: txFormattedLineData }],
            options: {
              ...txChartData.options,
              xaxis: {
                ...txChartData.options.xaxis,
                categories: txFormattedLineData,
                max: txLastdate.x.getTime(),
              },
            },
          });
        }

        const rxLineResponse = await axios.post(`/v2/op/device/${deviceId}/device-metrics/rxBytes?startDate=${minX}&endDate=${maxX}`, {
          groupBy: rxZoomLevel,
        });

        let rxFormattedLineData: DataPoint[] = []

        for (let i = 0; i < rxLineResponse.data.length; i++) {
          let date = new Date(rxLineResponse.data[i].time_group)
          date.setHours(date.getHours() + 1)
          rxFormattedLineData.push({ x: date, y: rxLineResponse.data[i].rxBytes })
        }

        let lastdate = rxFormattedLineData[rxFormattedLineData.length - 1]

        if (rxFormattedLineData.length === 1) {
          setRxChartData({
            ...txChartData,
            series: [{ name: 'rxBytes', data: rxFormattedLineData }],
            options: {
              ...txChartData.options,
              xaxis: {
                ...txChartData.options.xaxis,
                categories: rxFormattedLineData,
                max: new Date(maxX).getTime(),
              },
            },
          });
        } else {
          setRxChartData({
            ...txChartData,
            series: [{ name: 'rxBytes', data: rxFormattedLineData }],
            options: {
              ...txChartData.options,
              xaxis: {
                ...txChartData.options.xaxis,
                categories: rxFormattedLineData,
                max: lastdate.x.getTime(),
              },
            },
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setGraphLoading(false);
      }
    };

    fetchGraphData();
  }, [txZoomRange, rxZoomLevel, deviceId, minX, maxX, txZoomLevel]);


  // useEffect(() => {
  //   const fetchPieData = async () => {
  //     try {
  //       const pieResponse = await axios.post(`/v2/op/device/${deviceId}/device-metrics/pie`);

  //       setSignalCaptured(pieResponse.data.signal)
  //       let signalStrength = 0
  //       if (pieResponse.data.signal === 'weak') {
  //         signalStrength = 20
  //       } else if (pieResponse.data.signal === 'medium') {
  //         signalStrength = 45
  //       } else {
  //         signalStrength = 80
  //       }

  //       setTwoBarPie((prevState) => ({
  //         ...prevState,
  //         series: [pieResponse.data.txBytes, pieResponse.data.rxBytes],
  //         options: {
  //           colors: [colourChecker(pieResponse.data.txBytes), colourChecker(pieResponse.data.rxBytes)]
  //         }
  //       }))

  //       setOneChartData((prevState) => ({
  //         ...prevState,
  //         series: [signalStrength],
  //         options: {
  //           colors: [colourChecker(signalStrength)]
  //         }
  //       }))

  //       const formattedDate = new Date(pieResponse.data.createdAt).toLocaleString('en-UK');
  //       setTimeCaptured(formattedDate)
  //     } catch (error) {
  //       console.error('Error fetching data:', error);
  //     }
  //   }

  //   fetchPieData();
  // }, [])
  useEffect(() => {
    const fetchPieData = async () => {
      try {
        const pieResponse = await axios.post(`/v2/op/device/${deviceId}/device-metrics/pie`);
        const signal = pieResponse.data.metrics.signal;
        setMaxTxBitrate(pieResponse?.data?.max?.maxTxBitrate)
        setMaxRxBitrate(pieResponse?.data?.max?.maxRxBitrate)
        // Store the raw signal; the dBm value and localized description are derived at render
        setSignalCaptured(`${signal}`);
        setTxCaptured(`${pieResponse.data.metrics.txBitrate}`)
        setRxCaptured(`${pieResponse.data.metrics.rxBitrate}`)
        // Get appropriate signal strength
        const signalStrength = getSignalStrength(signal);

        setTwoBarPie((prevState) => ({
          ...prevState,
          series: [pieResponse.data.metrics.txBitrate, pieResponse.data.metrics.rxBitrate],
          options: {
            colors: [colourCheckerTxBitrates(pieResponse.data.metrics.txBitrate), colourCheckerRxBitrates(pieResponse.data.metrics.rxBitrate)]
          }
        }));

        setOneChartData((prevState) => ({
          ...prevState,
          series: [signalStrength],
          options: {
            colors: [colourChecker(signalStrength)]
          }
        }));

        // Raw timestamp; formatted at render with the active language's locale
        setTimeCaptured(pieResponse.data.metrics.createdAt ?? '');
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setPieLoading(false);
      }
    };
    fetchPieData();
  }, []);

  function getSignalDescription(signal: number): string {
    if (signal >= -40) return translate('deviceDetails.metrics.signal.extremelyStrong');
    if (signal >= -50) return translate('deviceDetails.metrics.signal.veryStrong');
    if (signal >= -60) return translate('deviceDetails.metrics.signal.strong');
    if (signal >= -70) return translate('deviceDetails.metrics.signal.good');
    if (signal >= -80) return translate('deviceDetails.metrics.signal.fair');
    if (signal >= -90) return translate('deviceDetails.metrics.signal.weak');
    return translate('deviceDetails.metrics.signal.veryPoor');
  }

  function getSignalStrength(signal: number): number {
    if (signal <= -90) return 10;   // Very poor signal  
    if (signal <= -80) return 20;   // Weak signal  
    if (signal <= -70) return 30;   // Fair signal  
    if (signal <= -60) return 45;   // Good signal  
    if (signal <= -50) return 65;   // Strong signal  
    if (signal <= -40) return 85;   // Very strong signal  
    return 100;                     // Extremely strong signal  
  }

  useEffect(() => {
    const determineZoomLevel = () => {
      if (txZoomRange <= 0.035) {
        return 'minute';
      } else if (txZoomRange <= 0.1) {
        return '5min';
      } else if (txZoomRange <= 0.2) {
        return '30min';
      } else if (txZoomRange <= 1.5) {
        return 'hour';
      } else if (txZoomRange <= 4) {
        return 'day';
      } else if (txZoomRange <= 9) {
        return 'week';
      } else if (txZoomRange <= 180) {
        return 'week';
      }
      return 'week';
    };

    setTxZoomLevel(determineZoomLevel());
  }, [txZoomRange]);

  useEffect(() => {
    const determineZoomLevel = () => {
      if (rxZoomRange <= 0.035) {
        return 'minute';
      } else if (rxZoomRange <= 0.1) {
        return '5min';
      } else if (rxZoomRange <= 0.2) {
        return '30min';
      } else if (rxZoomRange <= 1.5) {
        return 'hour';
      } else if (rxZoomRange <= 4) {
        return 'day';
      } else if (rxZoomRange <= 9) {
        return 'week';
      }
      return 'week';
    };

    setRxZoomLevel(determineZoomLevel());
  }, [rxZoomRange]);

  const zoomIn = (chartContext: any, { xaxis }: any) => {
    console.log('zoomIn')
    if (xaxis.min && xaxis.max) {
      const durationMs = xaxis.max - xaxis.min;
      const oneDayInMs = 24 * 60 * 60 * 1000;
      const durationInDays = durationMs / oneDayInMs;
      setTxZoomRange(durationInDays);
      setRxZoomRange(durationInDays);

      // console.log("durationInDays", durationInDays)

      let minDate = new Date(xaxis.min);
      let maxDate = new Date(xaxis.max);

      // console.log("minDate", minDate.toISOString())
      // console.log("maxDate", maxDate.toISOString())

      setMinX(minDate.toISOString());
      setMaxX(maxDate.toISOString());
      setActiveRange(null);
    }
  };

  // function colourChecker(data: any) {
  //   if (data <= 25) {
  //     return '#ff0000'
  //   } else if (data < 50) {
  //     return '#ffff00'
  //   } else if (data < 75) {
  //     return '#3BFF00'
  //   } else {
  //     return '#3BFF00'
  //   }
  // }

  function colourChecker(signal: number): string {
    if (signal <= -90) return '#ff0000';  // Very poor signal (Red)
    if (signal <= -80) return '#ff4500';  // Weak signal (Orange-Red)
    if (signal <= -70) return '#ffa500';  // Fair signal (Orange)
    if (signal <= -60) return '#ffff00';  // Moderate signal (Yellow)
    if (signal <= -50) return '#ADFF2F';  // Good signal (Green-Yellow)
    if (signal <= -40) return '#00FF00';  // Strong signal (Green)
    return '#00C000';                     // Extremely strong signal (Dark Green)
  }
  function colourCheckerTxBitrates(signal: number): string {
    if (((signal / maxTxBitrate) * 100) <= 30) return '#ff0000';  // Very poor signal (Red)
    if (((signal / maxTxBitrate) * 100) <= 40) return '#ff4500';  // Weak signal (Orange-Red)
    if (((signal / maxTxBitrate) * 100) <= 50) return '#ffa500';  // Fair signal (Orange)
    if (((signal / maxTxBitrate) * 100) <= 60) return '#ffff00';  // Moderate signal (Yellow)
    if (((signal / maxTxBitrate) * 100) <= 80) return '#ADFF2F';  // Good signal (Green-Yellow)
    if (((signal / maxTxBitrate) * 100) <= 100) return '#00FF00';  // Strong signal (Green)
    return '#00C000';                     // Extremely strong signal (Dark Green)
  }
  function colourCheckerRxBitrates(signal: number): string {
    if (((signal / maxRxBitrate) * 100) <= 30) return '#ff0000';  // Very poor signal (Red)
    if (((signal / maxRxBitrate) * 100) <= 40) return '#ff4500';  // Weak signal (Orange-Red)
    if (((signal / maxRxBitrate) * 100) <= 50) return '#ffa500';  // Fair signal (Orange)
    if (((signal / maxRxBitrate) * 100) <= 60) return '#ffff00';  // Moderate signal (Yellow)
    if (((signal / maxRxBitrate) * 100) <= 80) return '#ADFF2F';  // Good signal (Green-Yellow)
    if (((signal / maxRxBitrate) * 100) <= 100) return '#00FF00';  // Strong signal (Green)
    return '#00C000';                     // Extremely strong signal (Dark Green)
  }

  const handleDateRangeChange = () => {
    setMinX(new Date(new Date().getTime() - 24 * 60 * 60 * 1000).toISOString());
    setMaxX(new Date().toISOString());

    setTxZoomLevel('hour')
    setRxZoomLevel('hour')
    setActiveRange(24);
  };

  // Quick window: same state the drag-zoom drives, so groupBy adapts via the zoom-level effects
  const setRange = (hours: number, zoom: number) => {
    const now = new Date();
    setMinX(new Date(now.getTime() - hours * 60 * 60 * 1000).toISOString());
    setMaxX(now.toISOString());
    setTxZoomRange(zoom);
    setRxZoomRange(zoom);
    setActiveRange(hours);
  };

  const [oneChartData, setOneChartData] = useState({
    series: [0],
    options: {
      plotOptions: {
        radialBar: {
          offsetY: 0,
          startAngle: -90,
          endAngle: 90,
          hollow: {
            margin: 5,
            size: '50%',
            background: 'transparent',
            image: undefined,
          },
          dataLabels: {
            name: {
              show: false,
            },
            value: {
              show: false,
            }
          },
        }
      },
      colors: ['#3BFF00'],
      responsive: [{
        breakpoint: 480,
        options: {
          legend: {
            show: false
          }
        }
      }]
    } as ApexOptions,
  })

  const [twoBarPie, setTwoBarPie] = useState({
    series: [0, 0],
    options: {
      plotOptions: {
        radialBar: {
          offsetY: 0,
          startAngle: -90,
          endAngle: 90,
          hollow: {
            margin: 5,
            size: '30%',
            background: 'transparent',
            image: undefined,
          },
          dataLabels: {
            name: {
              show: false,
            },
            value: {
              show: false,
            }
          },
        },
      },
      colors: ['#3BFF00', '#FFFC00'],
      responsive: [{
        breakpoint: 480,
        options: {
          legend: {
            show: false
          }
        }
      }]
    } as ApexOptions,
  });

  const [txChartData, setTxChartData] = useState({
    series: [
      {
        name: 'txBytes',
        data: [] as DataPoint[],
      },
    ],
    options: {
      chart: {
        zoom: {
          type: 'x',
          enabled: true,
          autoScaleYaxis: true,
        },
        toolbar: {
          autoSelected: 'zoom',
          tools: {
            reset: false,
          },
        },
        events: {
          zoomed: (chartContext: any, { xaxis }: any) => zoomIn(chartContext, { xaxis }),
        },
      },
      colors: ['#003D4C'],
      dataLabels: {
        enabled: false,
        offsetY: 0,
        style: {
          fontSize: '8px',
          colors: ['#304758'],
        },
      },
      xaxis: {
        type: 'datetime',
        max: new Date(maxX).getTime(),
        axis : { show: false },
        axisTicks: { show: false },
        crosshairs: {
          fill: {
            type: 'gradient',
            gradient: {
              colorFrom: '#D8E3F0',
              colorTo: '#BED1E6',
              stops: [0, 100],
              opacityFrom: 0.4,
              opacityTo: 0.5,
            },
          },
        },
      },
      markers: {
        size: 4,
        colors: ['#003D4C'],
        strokeColors: '#003D4C',
        strokeWidth: 2,
      },
      tooltip: {
        custom: function ({ series, dataPointIndex }: any) {
          return series
            .map((s: any, index: any) => {
              const value = s[dataPointIndex] !== undefined ? s[dataPointIndex] : 0;
              const color = ['#003D4C'];
              return `
              <div style="display: flex; align-items: center; margin-bottom: 5px;">
                <span style="background-color: ${color};
                             width: 10px;
                             height: 10px;
                              -radius: 50%;
                             display: inline-block;
                             margin-right: 5px;">
                </span>
                <span style="color: #10182080; font-weight: 650;">${value} Bytes</span>
              </div>
            `;
            })
            .join('');
        },
      },
      yaxis: {
        axis : { show: false },
        axisTicks: { show: false },
        labels: {
          formatter: function (value: number) {
            return value.toExponential(1); // Converts to scientific notation (e.g., 1.0e+3)
          },
        },

        grid: { show: false },
      },
      legend: { show: false },
      stroke: { width: 3 },
    } as ApexOptions,
  });

  const [rxChartData, setRxChartData] = useState({
    series: [
      {
        name: 'rxBytes',
        data: [] as DataPoint[],
      },
    ],
    options: {
      chart: {
        zoom: {
          type: 'x',
          enabled: true,
          autoScaleYaxis: true,
        },
        toolbar: {
          autoSelected: 'zoom',
          tools: {
            reset: false,
          },
        },
        events: {
          zoomed: (chartContext: any, { xaxis }: any) => zoomIn(chartContext, { xaxis }),
        },
      },
      colors: ['#003D4C'],
      dataLabels: {
        enabled: false,
        offsetY: 0,
        style: {
          fontSize: '8px',
          colors: ['#304758'],
        },
      },
      xaxis: {
        type: 'datetime',
        max: new Date(maxX).getTime(),
        axis : { show: false },
        axisTicks: { show: false },
        crosshairs: {
          fill: {
            type: 'gradient',
            gradient: {
              colorFrom: '#D8E3F0',
              colorTo: '#BED1E6',
              stops: [0, 100],
              opacityFrom: 0.4,
              opacityTo: 0.5,
            },
          },
        },
      },
      markers: {
        size: 4,
        colors: ['#003D4C'],
        strokeColors: '#003D4C',
        strokeWidth: 2,
      },
      tooltip: {
        custom: function ({ series, dataPointIndex }: any) {
          return series
            .map((s: any, index: any) => {
              const value = s[dataPointIndex] !== undefined ? s[dataPointIndex] : 0;
              const color = ['#003D4C'];
              return `
              <div style="display: flex; align-items: center; margin-bottom: 5px;">
                <span style="background-color: ${color};
                             width: 10px;
                             height: 10px;
                              -radius: 50%;
                             display: inline-block;
                             margin-right: 5px;">
                </span>
                <span style="color: #10182080; font-weight: 650;">${value} Bytes</span>
              </div>
            `;
            })
            .join('');
        },
      },
      yaxis: {
        axis : { show: false },
        axisTicks: { show: false },
        labels: { show: true },
        grid: { show: false },
      },
      legend: { show: false },
      stroke: { width: 3 },
    } as ApexOptions,
  });

  // ---- Theme-aware apex options, derived at render time from the stored options ----
  const t = chartTokens[theme];
  const infoBlue = INFO_BLUE[theme];

  const themedLine = (base: ApexOptions, color: string): ApexOptions => {
    const baseY = base.yaxis as ApexYAxis | undefined;
    return {
      ...base,
      chart: {
        ...base.chart,
        background: 'transparent',
        foreColor: t.mute,
        fontFamily: fonts.sans,
      },
      theme: { mode: theme },
      colors: [color],
      grid: { borderColor: t.border, strokeDashArray: 3 },
      xaxis: {
        ...base.xaxis,
        labels: { ...base.xaxis?.labels, style: { colors: t.mute, fontFamily: fonts.mono, fontSize: '10.5px' } },
        axisBorder: { show: true, color: t.border },
        crosshairs: { stroke: { color: t.border, width: 1, dashArray: 3 } },
      },
      yaxis: {
        ...baseY,
        labels: {
          ...baseY?.labels,
          formatter: (value: number) => formatBytes(value),
          style: { colors: t.mute, fontFamily: fonts.mono, fontSize: '10.5px' },
        },
      },
      markers: {
        ...base.markers,
        colors: [color],
        strokeColors: color,
      },
      tooltip: {
        ...base.tooltip,
        theme,
        custom: function ({ series, dataPointIndex }: any) {
          return series
            .map((s: any) => {
              const value = s[dataPointIndex] !== undefined ? s[dataPointIndex] : 0;
              return `
                <div style="display:flex;align-items:center;gap:6px;padding:6px 10px;font-family:${fonts.mono};font-size:12px;">
                  <span style="background-color:${color};width:8px;height:8px;border-radius:50%;display:inline-block;"></span>
                  <span style="color:${t.text};font-weight:500;">${formatBytes(Number(value))}</span>
                </div>
              `;
            })
            .join('');
        },
      },
    };
  };

  const themedRadial = (base: ApexOptions, colors: string[], hollow: string): ApexOptions => ({
    ...base,
    chart: { ...base.chart, background: 'transparent', foreColor: t.mute, fontFamily: fonts.sans },
    theme: { mode: theme },
    colors,
    stroke: { lineCap: 'round' },
    plotOptions: {
      radialBar: {
        offsetY: 0,
        startAngle: -90,
        endAngle: 90,
        hollow: { margin: 5, size: hollow, background: 'transparent' },
        track: { background: t.track, strokeWidth: '100%' },
        dataLabels: { name: { show: false }, value: { show: false } },
      },
    },
    tooltip: { theme },
  });

  // Signal gauge colour by strength bucket (see getSignalStrength): Good+ / Fair / Weak.
  const signalStrength = oneChartData.series[0] ?? 0;
  const signalTone: 'ok' | 'warn' | 'danger' = signalStrength >= 45 ? 'ok' : signalStrength >= 30 ? 'warn' : 'danger';
  const signalColor = signalTone === 'ok' ? t.accent : signalTone === 'warn' ? t.warn : t.danger;

  const signalOptions = themedRadial(oneChartData.options, [signalColor], '50%');
  const bitrateOptions = themedRadial(twoBarPie.options, [t.accent, infoBlue], '30%');
  const pctOfMax = (value: number, max: number) =>
    max > 0 ? Math.min(100, Math.round((Number(value) / max) * 100)) : Math.min(100, Number(value) || 0);
  const hasBitrateMax = maxTxBitrate > 0 && maxRxBitrate > 0;
  const bitrateSeries = [pctOfMax(twoBarPie.series[0], maxTxBitrate), pctOfMax(twoBarPie.series[1], maxRxBitrate)];

  const totalBytes = (series: typeof txChartData.series) =>
    series[0].data.reduce((sum: number, point: any) => sum + (Number(point?.y) || 0), 0);
  const activeRangeDef = RANGES.find((r) => r.hours === activeRange);
  const rangeLabel =
    activeRange === null
      ? translate('deviceDetails.metrics.customRange')
      : activeRangeDef
        ? translate(`deviceDetails.metrics.ranges.${activeRangeDef.label}`)
        : '';
  const txOptions = themedLine(txChartData.options, t.accent);
  const rxOptions = themedLine(rxChartData.options, infoBlue);

  const signalValue = signalCaptured ? `${signalCaptured} dBm` : '';
  const signalDesc = signalCaptured ? getSignalDescription(Number(signalCaptured)) : '';
  const signalText = signalCaptured ? `${signalValue} - ${signalDesc}` : '';
  const dash = '—';
  const timeCapturedText = timeCaptured ? new Date(timeCaptured).toLocaleString(getLanguage(i18n.language).locale) : '';

  const renderLineBody = (series: typeof txChartData.series, options: ApexOptions, loadingText: string, emptyText: string) => {
    if (graphLoading && series[0].data.length === 0) {
      return (
        <div className="dd-metrics-state">
          <span className="bd-spinner" />
          <span className="bd-meta">{loadingText}</span>
        </div>
      );
    }
    if (series[0].data.length === 0) {
      return (
        <div className="dd-metrics-state">
          <span className="bd-meta">{emptyText}</span>
        </div>
      );
    }
    return <ReactApexChart key={theme} options={options} series={series} type="line" height={220} />;
  };

  const renderReset = (ariaLabel: string) => (
    <button
      type="button"
      className="bd-iconbtn dd-metrics-reset"
      onClick={() => handleDateRangeChange()}
      aria-label={ariaLabel}
      title={translate('deviceDetails.metrics.resetTitle')}
    >
      <RotateCcw size={15} strokeWidth={1.9} />
    </button>
  );

  return (
    <section className="dd-metrics">
      <header className="dd-metrics-hd">
        <div className="dd-metrics-hd-text">
          <h2 className="bd-h2">{translate('deviceDetails.metrics.title')}</h2>
          <p className="bd-meta">{translate('deviceDetails.metrics.description')}</p>
        </div>
        <div className="dd-metrics-hd-tools">
          {graphLoading && (
            <span className="bd-chip">
              <span className="bd-spinner" /> {translate('deviceDetails.metrics.updating')}
            </span>
          )}
          <div className="bd-segmented dd-metrics-range" role="group" aria-label={translate('deviceDetails.metrics.rangeAriaLabel')}>
            {RANGES.map((range) => (
              <button
                key={range.hours}
                type="button"
                className={activeRange === range.hours ? 'active' : ''}
                aria-pressed={activeRange === range.hours}
                onClick={() => setRange(range.hours, range.zoom)}
              >
                {translate(`deviceDetails.metrics.ranges.${range.label}`)}
              </button>
            ))}
            {activeRange === null && (
              <button type="button" className="active" aria-pressed="true" disabled>
                {translate('deviceDetails.metrics.custom')}
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="dd-metrics-kpis">
        <div className={`bd-card bd-kpi${signalCaptured && signalTone !== 'ok' ? ` ${signalTone}` : ''}`}>
          <span className="bd-kpi-icon"><Wifi size={17} strokeWidth={1.9} /></span>
          <div className="bd-kpi-text">
            <span className="bd-kpi-label">{translate('deviceDetails.metrics.signalStrength')}</span>
            <span className="bd-stat-num bd-kpi-num">{signalValue || dash}</span>
            {signalDesc && <span className="bd-meta dd-metrics-kpi-sub">{signalDesc}</span>}
          </div>
        </div>
        <div className="bd-card bd-kpi">
          <span className="bd-kpi-icon"><ArrowUp size={17} strokeWidth={1.9} /></span>
          <div className="bd-kpi-text">
            <span className="bd-kpi-label">{translate('deviceDetails.metrics.txBitrate')}</span>
            <span className="bd-stat-num bd-kpi-num">{txCaptured || dash}</span>
          </div>
        </div>
        <div className="bd-card bd-kpi dd-metrics-kpi-info">
          <span className="bd-kpi-icon"><ArrowDown size={17} strokeWidth={1.9} /></span>
          <div className="bd-kpi-text">
            <span className="bd-kpi-label">{translate('deviceDetails.metrics.rxBitrate')}</span>
            <span className="bd-stat-num bd-kpi-num">{rxCaptured || dash}</span>
          </div>
        </div>
        <div className="bd-card bd-kpi dd-metrics-kpi-muted">
          <span className="bd-kpi-icon"><Clock size={17} strokeWidth={1.9} /></span>
          <div className="bd-kpi-text">
            <span className="bd-kpi-label">{translate('deviceDetails.metrics.lastCaptured')}</span>
            <span className="bd-mono dd-metrics-kpi-date">{timeCapturedText || (pieLoading ? translate('deviceDetails.metrics.loading') : dash)}</span>
          </div>
        </div>
      </div>

      <div className="dd-metrics-grid">
        <article className="dd-metrics-card">
          <div className="dd-metrics-card-hd">
            <div>
              <h3 className="bd-h3">{translate('deviceDetails.metrics.signalStrength')}</h3>
              <span className="bd-meta"><Trans i18nKey="deviceDetails.metrics.capturedOn" values={{ date: timeCapturedText || dash }} components={{ mono: <span className="bd-mono" /> }} /></span>
            </div>
          </div>
          {pieLoading ? (
            <div className="dd-metrics-state"><span className="bd-spinner" /></div>
          ) : (
            <div className="dd-metrics-gauge">
              <ReactApexChart key={theme} options={signalOptions} series={oneChartData.series} type="radialBar" height={200} />
              <div className="dd-metrics-gauge-label">
                <span className="bd-mono">{signalText || translate('deviceDetails.metrics.noSignalData')}</span>
              </div>
              <div className="dd-metrics-scale" aria-hidden="true">
                {SIGNAL_SCALE.map((label, index) => (
                  <span
                    key={label}
                    className={`dd-metrics-scale-step s${index}${
                      signalStrength > 0 &&
                      (signalStrength < 30 ? 0 : signalStrength < 45 ? 1 : signalStrength < 65 ? 2 : 3) === index
                        ? ' current'
                        : ''
                    }`}
                  >
                    {translate(`deviceDetails.metrics.scale.${label.toLowerCase()}`)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>

        <article className="dd-metrics-card">
          <div className="dd-metrics-card-hd">
            <div>
              <h3 className="bd-h3">{translate('deviceDetails.metrics.packetsTransmitted')}</h3>
              <span className="bd-meta"><Trans i18nKey="deviceDetails.metrics.lastCapturedOn" values={{ date: timeCapturedText || dash }} components={{ mono: <span className="bd-mono" /> }} /></span>
            </div>
            <div className="dd-metrics-card-tools">
              {txChartData.series[0].data.length > 0 && (
                <span className="bd-chip" title={translate('deviceDetails.metrics.totalFor', { range: rangeLabel })}>
                  <Sigma size={12} />
                  <span className="bd-mono">{formatBytes(totalBytes(txChartData.series))}</span>
                </span>
              )}
              {renderReset(translate('deviceDetails.metrics.resetTxAria'))}
            </div>
          </div>
          {renderLineBody(
            txChartData.series,
            txOptions,
            translate('deviceDetails.metrics.loadingTx'),
            translate('deviceDetails.metrics.noDataTx')
          )}
        </article>

        <article className="dd-metrics-card">
          <div className="dd-metrics-card-hd">
            <div>
              <h3 className="bd-h3">{translate('deviceDetails.metrics.bitrates')}</h3>
              <span className="bd-meta"><Trans i18nKey="deviceDetails.metrics.capturedOn" values={{ date: timeCapturedText || dash }} components={{ mono: <span className="bd-mono" /> }} /></span>
            </div>
          </div>
          <div className="dd-metrics-legend">
            <div className="dd-metrics-legend-row">
              <span className="dd-metrics-dot tx" />
              <span className="bd-field-label">{translate('deviceDetails.metrics.txBitrate')}</span>
              <span className="bd-mono">{txCaptured || dash}</span>
            </div>
            <div className="dd-metrics-legend-row">
              <span className="dd-metrics-dot rx" />
              <span className="bd-field-label">{translate('deviceDetails.metrics.rxBitrate')}</span>
              <span className="bd-mono">{rxCaptured || dash}</span>
            </div>
          </div>
          {pieLoading ? (
            <div className="dd-metrics-state"><span className="bd-spinner" /></div>
          ) : (
            <>
              <ReactApexChart key={theme} options={bitrateOptions} series={bitrateSeries} type="radialBar" height={200} />
              <span className="bd-meta dd-metrics-gauge-note">
                {hasBitrateMax
                  ? translate('deviceDetails.metrics.ringsPct', { tx: bitrateSeries[0], rx: bitrateSeries[1] })
                  : translate('deviceDetails.metrics.ringsRaw')}
              </span>
            </>
          )}
        </article>

        <article className="dd-metrics-card">
          <div className="dd-metrics-card-hd">
            <div>
              <h3 className="bd-h3">{translate('deviceDetails.metrics.packetsReceived')}</h3>
              <span className="bd-meta"><Trans i18nKey="deviceDetails.metrics.lastCapturedOn" values={{ date: timeCapturedText || dash }} components={{ mono: <span className="bd-mono" /> }} /></span>
            </div>
            <div className="dd-metrics-card-tools">
              {rxChartData.series[0].data.length > 0 && (
                <span className="bd-chip" title={translate('deviceDetails.metrics.totalFor', { range: rangeLabel })}>
                  <Sigma size={12} />
                  <span className="bd-mono">{formatBytes(totalBytes(rxChartData.series))}</span>
                </span>
              )}
              {renderReset(translate('deviceDetails.metrics.resetRxAria'))}
            </div>
          </div>
          {renderLineBody(
            rxChartData.series,
            rxOptions,
            translate('deviceDetails.metrics.loadingRx'),
            translate('deviceDetails.metrics.noDataRx')
          )}
        </article>
      </div>
    </section>
  );
};
